import React from 'react';
import T from 'Components/Helpers/translate';
import CompanyCard from './CompanyCard';
import PersonCard from './PersonCard';
import { LINK } from './LookupParts';
import { useSelf } from 'App/components/hooks/appHooks';
import {
	PROVIDER_NAMES,
	STATUS_KEYS,
	type LookupProvider,
	type LookupQueryKind,
	type LookupResult,
	type PhoneOnDemand
} from './lookupApi';

const MESSAGE =
	'rounded-[8px] border border-dashed border-grey-5 bg-background p-5 text-center text-body-md text-grey-11';

const NEEDS_SOURCE: Partial<Record<LookupQueryKind, string>> = {
	linkedinUrl: 'prospectingTools.lookup.needs.source.linkedinUrl',
	domain: 'prospectingTools.lookup.needs.source.domain',
	email: 'prospectingTools.lookup.needs.source.email',
	nameAndCompany: 'prospectingTools.lookup.needs.source.nameAndCompany'
};

/** "Apollo", "Apollo or Hunter", "Apollo, Hunter or Lusha". */
const providerList = (needs: LookupProvider[]) => {
	const names = needs.map(provider => PROVIDER_NAMES[provider]);
	const last = names.pop();
	return names.length ? `${names.join(', ')} ${T('prospectingTools.lookup.needs.or')} ${last}` : last;
};

/** `noConnectors`: which provider the lookup needs, and where an admin connects it (WEB-13097). */
const NoConnectorsMessage = ({ result }: { result: LookupResult }) => {
	const self = useSelf();
	const needs = result.needs ?? [];
	const source = NEEDS_SOURCE[result.kind];
	if (!needs.length || !source) {
		return (
			<p role="status" className={MESSAGE}>
				{T(STATUS_KEYS.noConnectors)}
			</p>
		);
	}
	return (
		<p role="status" className={MESSAGE}>
			{T(needs.length === 1 ? 'prospectingTools.lookup.needs.one' : 'prospectingTools.lookup.needs.any', {
				source: T(source),
				providers: providerList(needs)
			})}{' '}
			{self?.administrator ? (
				<a href="#/admin/prospecting-enrichment" className={LINK}>
					{T('prospectingTools.lookup.needs.adminLink')}
				</a>
			) : (
				T('prospectingTools.lookup.needs.askAdmin')
			)}
		</p>
	);
};

/**
 * A lookup's answer: its status message, or the person / company card. Supersearch's lookup panel
 * shows it (WEB-13132); the Lookup tab it was built for is gone (WEB-13187).
 */
type ResultViewProps = {
	result: LookupResult;
	onLookUp: (query: string) => void;
	/** Phone on demand for a person (WEB-13365); absent where the lookup asked for the phone up front. */
	phone?: PhoneOnDemand;
};

export const ResultView = ({ result, onLookUp, phone }: ResultViewProps) => {
	if (result.status === 'noConnectors') return <NoConnectorsMessage result={result} />;
	if (result.status !== 'found') {
		return (
			<p role="status" className={MESSAGE}>
				{T(STATUS_KEYS[result.status])}
			</p>
		);
	}
	if (result.person) {
		return (
			<div className="flex flex-col gap-3">
				{result.skippedForCredits ? (
					<p role="status" className="rounded-[8px] bg-alert-light p-3 text-body-md text-alert-dark">
						{T('prospectingTools.lookup.skippedForCredits')}
					</p>
				) : null}
				{result.outsideEuOwnKeys?.length ? (
					<p role="status" className="rounded-[8px] bg-alert-light p-3 text-body-md text-alert-dark">
						{T('prospectingTools.lookup.outsideEuOwnKeys', {
							providers: result.outsideEuOwnKeys.map(provider => PROVIDER_NAMES[provider]).join(', ')
						})}
					</p>
				) : null}
				<PersonCard
					// Per query: a company added from one result is not the next person's (WEB-13921).
					key={result.query}
					person={result.person}
					company={result.company}
					aiCredits={result.aiCredits}
					onLookUp={onLookUp}
					phone={phone}
				/>
			</div>
		);
	}
	return result.company ? <CompanyCard company={result.company} onLookUp={onLookUp} /> : null;
};
